import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import commoncss from "../../../css/common.module.css";
import buttoncss from "../../../css/button.module.css";
import Modal from "../../../elements/Modal";
import { Button } from "../../../elements/Button";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";
import { ReactComponent as SearchIcon } from "../../../assets/icons/searchPri.svg";
import emptyState from "../../../assets/icons/Empty State.png";


const ExistingPresetModal = ({
  isOpen,
  onClose,
  onSave,
  presetList = [],
  workspaceId = "",
}) => {
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [displayPresets, setDisplayPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingPresets, setFetchingPresets] = useState(false);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const searchButtonRef = useRef(null);

  const workspace_data = useSelector((state) => state.WSReducer);
  const { projectData } = useSelector((state) => state.ADJStandaloneReducer || {});
  const resolvedWorkspaceId =
    workspaceId ||
    projectData?.workspace_id ||
    workspace_data?.response?.[0]?._id ||
    localStorage.getItem("standalone_workspace_id") ||
    localStorage.getItem("ws_id") ||
    "";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showSearch &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target) &&
        searchButtonRef.current &&
        !searchButtonRef.current.contains(event.target)
      ) {
        setShowSearch(false);
      }
    };

    if (showSearch) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearch]);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedIds([]);
    setSearchTerm("");
    setShowSearch(false);
    setLoading(false);

    if (presetList && presetList.length > 0) {
      const formatted = presetList.map((item, idx) => ({
        id: item._id || item.id || item.value || `preset_${idx}`,
        name: item.name || item.label || item.preset_name || item.presetName || item.project_name || item.projectName || "Unnamed Preset",
      }));
      setDisplayPresets(formatted);
      return;
    }

    let isMounted = true;
    const fetchPresets = async () => {
      setFetchingPresets(true);
      try {
        const auth = localStorage.getItem("auth_token");
        const headers = { "x-auth-token": auth };
        const wId = resolvedWorkspaceId || "";
        let url = wId
          ? `/api/v1/presets/list-presets?workspace_id=${wId}`
          : `/api/v1/presets/list-presets`;
        let res = await axios.get(url, { headers });
        let list =
          res?.data?.project ||
          res?.data?.presets_list ||
          res?.data?.presets ||
          res?.data?.preset_list ||
          res?.data?.data ||
          (Array.isArray(res?.data) ? res?.data : []);

        if (isMounted) {
          if (Array.isArray(list) && list.length > 0) {
            const formatted = list.map((item, idx) => ({
              id: item._id || item.id || item.value || `preset_${idx}`,
              name: item.preset_name || item.presetName || item.project_name || item.projectName || item.name || item.label || "Unnamed Preset",
            }));
            setDisplayPresets(formatted);
          } else {
            setDisplayPresets([]);
          }
        }
      } catch (err) {
        console.error("Failed to fetch presets list", err);
        if (isMounted) {
          setDisplayPresets([]);
        }
      } finally {
        if (isMounted) {
          setFetchingPresets(false);
        }
      }
    };

    fetchPresets();

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  if (!isOpen) return null;

  const filteredPresets = displayPresets.filter((p) =>
    p.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    if (selectedIds.length === 0 || loading) return;
    const selectedObjects = displayPresets.filter((p) =>
      selectedIds.includes(p.id)
    );
    if (onSave) {
      setLoading(true);
      try {
        const result = await onSave(selectedObjects);
        if (result !== false) {
          onClose();
        }
      } catch (error) {
        console.error("Error saving preset:", error);
      } finally {
        setLoading(false);
      }
    } else {
      onClose();
    }
  };

  return (
    <Modal
      title="Existing Preset"
      bodycss={commoncss.unsetPadding}
      width="560px"
      minHeight="490px"
      bodyStyle={{ display: "flex", flexDirection: "column", height: "100%" }}
      headerActions={
        <CloseIcon
          onClick={loading ? undefined : onClose}
          className="clickable"
          style={{ cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}
        />
      }
      onClose={loading ? () => {} : onClose}
      footerActions={
        <>
          <Button
            btnColor={buttoncss.secondary}
            onClick={onClose}
            disabled={loading}
            label="Cancel"
          />
          <Button
            btnColor={buttoncss.primary}
            label={loading ? "Saving..." : "Save"}
            disabled={selectedIds.length === 0 || loading}
            onClick={handleSave}
          />
        </>
      }
    >
      <style>{`
        .preset-modal-list::-webkit-scrollbar {
          display: none;
        }
        .preset-modal-list {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      <div style={{ padding: "16px 24px 20px 24px", display: "flex", flexDirection: "column", flex: 1, minHeight: "360px" }}>
        {/* Preset List (Without visible scrollbar, checkbox on left aligned straight) */}
        <div
          className="preset-modal-list"
          style={{
            height: "290px",
            minHeight: "290px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {fetchingPresets ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                minHeight: "260px",
                color: "#8FA39E",
                fontSize: "13px",
              }}
            >
              Loading presets...
            </div>
          ) : filteredPresets.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                minHeight: "260px",
                gap: "12px",
                textAlign: "center",
              }}
            >
              <img
                src={emptyState}
                alt="No Presets"
                style={{ width: "240px", objectFit: "contain" }}
              />
              <span style={{ fontSize: "14px", color: "#667085", fontWeight: 500 }}>
                {searchTerm ? `No presets found matching "${searchTerm}"` : "No presets found in this workspace"}
              </span>
            </div>
          ) : (
            filteredPresets.map((preset) => {
              const isChecked = selectedIds.includes(preset.id);
              return (
                <div
                  key={preset.id}
                  onClick={() => toggleSelect(preset.id)}
                  className="clickable"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "10px 14px",
                    borderRadius: "6px",
                    // border: `1px solid ${isChecked ? "#c2e6d1" : "#E2EBE8"}`,
                    // backgroundColor: isChecked ? "#f4faf5" : "#FAFDFB",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  {/* Checkbox on LEFT (aligned with Select All) */}
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleSelect(preset.id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      accentColor: "#205A46",
                      cursor: "pointer",
                      width: "16px",
                      height: "16px",
                      flexShrink: 0,
                      margin: 0,
                    }}
                  />
                  {/* Preset Name on RIGHT of checkbox */}
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: isChecked ? "500" : "400",
                      color: isChecked ? "#1B3B36" : "#334D47",
                    }}
                  >
                    {preset.name}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ExistingPresetModal;
